/**
 * Общий выбор из справочника (СПР-1b): контрагент или человек с поиском и
 * «Создать нового». Используется в карточке ИЛ (Заказчик), в замечании
 * (Организация экспертизы), в карточке аукциона (Заказчик) и в контактах ИЛ
 * (человек). Самодостаточный native <dialog class="app-modal"> → Promise.
 *
 * pickCounterparty({ role, title, currentId }) и pickPerson({ title, currentId,
 * counterpartyId }) резолвят:
 *   { action: 'select', id, name } — выбран/создан,
 *   { action: 'unlink' }           — «Отвязать» (если был currentId),
 *   null                           — отмена.
 * Создание: source='manual' (по умолчанию в API), роль проставляется.
 */
import { api } from '../api.js';
import { canDo } from '../auth.js';
import { showNotification } from './notification.js';

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const errText = (e) => e?.detail ?? e?.message ?? 'Ошибка';

function openPicker({ title, placeholder, currentId, allowCreate, search, createLabel, create }) {
  return new Promise((resolve) => {
    const dlg = document.createElement('dialog');
    dlg.className = 'app-modal cp-modal ref-picker';
    dlg.innerHTML = `
      <form method="dialog" class="modal-form">
        <h3 class="modal-title">${esc(title)}</h3>
        <input type="search" class="ref-picker-search" placeholder="${esc(placeholder)}" autocomplete="off">
        <div class="ref-picker-list" aria-live="polite"></div>
        <div class="modal-actions">
          ${currentId ? '<button type="button" class="dt-btn cp-danger" data-pick-unlink>Отвязать</button>' : ''}
          <button type="button" class="modal-cancel">Отмена</button>
        </div>
      </form>`;
    document.body.appendChild(dlg);
    const listEl = dlg.querySelector('.ref-picker-list');
    const searchEl = dlg.querySelector('.ref-picker-search');
    const close = (v) => { try { dlg.close(); } catch { /* закрыт */ } dlg.remove(); resolve(v); };

    dlg.querySelector('.modal-cancel').addEventListener('click', () => close(null));
    dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(null); });
    dlg.querySelector('[data-pick-unlink]')?.addEventListener('click', () => close({ action: 'unlink' }));

    let seq = 0;
    const render = (items, q) => {
      const rows = items.map(it => `
        <button type="button" class="ref-picker-item" data-pick-id="${it.id}" data-pick-name="${esc(it.name)}">
          <span class="ref-picker-item-name">${esc(it.name)}</span>
          ${it.sub ? `<span class="ref-picker-item-sub">${esc(it.sub)}</span>` : ''}
        </button>`).join('');
      const createBtn = (allowCreate && canDo('ref.write') && q.trim())
        ? `<button type="button" class="ref-picker-create" data-pick-create>${esc(createLabel)} «${esc(q.trim())}»</button>`
        : '';
      listEl.innerHTML = (rows || '<p class="ref-picker-empty">Ничего не найдено.</p>') + createBtn;
      listEl.querySelectorAll('[data-pick-id]').forEach(b => b.addEventListener('click', () =>
        close({ action: 'select', id: Number(b.dataset.pickId), name: b.dataset.pickName })));
      listEl.querySelector('[data-pick-create]')?.addEventListener('click', async (e) => {
        e.target.disabled = true;
        try {
          const created = await create(q.trim());
          showNotification('Создан и выбран', 'success');
          close({ action: 'select', id: created.id, name: created.name });
        } catch (err) { showNotification(errText(err), 'error'); e.target.disabled = false; }
      });
    };

    const load = async (q) => {
      const my = ++seq;
      listEl.innerHTML = '<p class="ref-picker-empty">Поиск…</p>';
      try {
        const items = await search(q);
        if (my === seq) render(items, q);
      } catch (e) { if (my === seq) listEl.innerHTML = `<p class="ref-picker-empty">${esc(errText(e))}</p>`; }
    };

    let timer = null;
    searchEl.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => load(searchEl.value), 250);
    });
    dlg.showModal();
    load('');
    setTimeout(() => searchEl.focus(), 0);
  });
}

/** Выбор контрагента (role фильтрует справочник и проставляется при создании). */
export function pickCounterparty({ role, title = 'Выбрать контрагента', currentId = null, allowCreate = true } = {}) {
  return openPicker({
    title,
    placeholder: 'Поиск по названию / ИНН',
    currentId,
    allowCreate,
    createLabel: 'Создать нового',
    search: async (q) => {
      const params = { active: true, limit: 50 };
      if (role) params.role = role;
      if (q.trim()) params.q = q.trim();
      const res = await api.refs.counterparties(params);
      return res.items.map(c => ({ id: c.id, name: c.name, sub: [c.inn, c.full_name].filter(Boolean).join(' · ') }));
    },
    create: async (name) => {
      const body = { name, roles: role ? [role] : [] };
      return api.refs.createCounterparty(body);
    },
  });
}

/** Выбор исполнительного листа (ИЛ) из справочника — для привязки загружаемого файла
 * замечаний (ЭКС-1b-3). Создание ИЛ — отдельно в разделе /projects/sheets, поэтому
 * allowCreate=false. Резолвит { action:'select', id, name } или null. */
export function pickSheet({ title = 'Выбрать исполнительный лист', currentId = null } = {}) {
  return openPicker({
    title,
    placeholder: 'Поиск по наименованию / № проекта / № ИП / договору',
    currentId,
    allowCreate: false,
    createLabel: '',
    search: async (q) => {
      const params = { limit: 50 };
      if (q.trim()) params.q = q.trim();
      const res = await api.expertise.sheets(params);
      return res.items.map(s => ({
        id: s.id,
        name: s.short_name || `лист #${s.id}`,
        sub: [s.ip_no, s.project_no, s.counterparty_name || s.counterparty].filter(Boolean).join(' · '),
      }));
    },
    create: null,
  });
}

/** Выбор человека (role — для создания; counterpartyId — привязка нового человека). */
export function pickPerson({ title = 'Выбрать человека', currentId = null, role = 'contact', counterpartyId = null } = {}) {
  return openPicker({
    title,
    placeholder: 'Поиск по ФИО / email / телефону',
    currentId,
    allowCreate: true,
    createLabel: 'Создать нового',
    search: async (q) => {
      const params = { active: true, limit: 50 };
      if (q.trim()) params.q = q.trim();
      const res = await api.refs.persons(params);
      return res.items.map(p => ({
        id: p.id, name: p.fio,
        sub: [p.position, p.counterparty_name, p.phone, p.email].filter(Boolean).join(' · '),
      }));
    },
    create: async (fio) => {
      const body = { fio, roles: role ? [role] : [] };
      if (counterpartyId) body.counterparty_id = counterpartyId;
      const p = await api.refs.createPerson(body);
      return { id: p.id, name: p.fio };
    },
  });
}
